/**
 * Client helpers for AI usage spend UI.
 * Month keys match functions/lib/aiUsageAggregation.js (UTC YYYY-MM).
 */

/**
 * @param {Date} [date]
 * @returns {string}
 */
export function utcMonthKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Inclusive start / exclusive end of the UTC calendar month for `date`.
 * @param {Date} [date]
 * @returns {{ start: Date, endExclusive: Date, monthKey: string }}
 */
export function currentMonthDateRangeUtc(date = new Date()) {
  const d = date instanceof Date ? date : new Date();
  const y = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const start = new Date(Date.UTC(y, month, 1, 0, 0, 0, 0));
  const endExclusive = new Date(Date.UTC(y, month + 1, 1, 0, 0, 0, 0));
  return { start, endExclusive, monthKey: utcMonthKey(d) };
}

/**
 * @param {unknown} n
 * @param {number} [digits]
 * @returns {string}
 */
export function formatUsd(n, digits = 2) {
  const value = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  return `$${value.toFixed(digits)}`;
}

/**
 * @param {unknown} raw
 * @returns {number | null}
 */
export function normalizeMonthlyAiBudgetUsd(raw) {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Alias — same validation for daily / weekly / per-month USD budgets. */
export const normalizeOptionalBudgetUsd = normalizeMonthlyAiBudgetUsd;

/**
 * Current UTC month + next 11 (12 keys total).
 * @param {Date} [date]
 * @param {number} [count]
 * @returns {string[]}
 */
export function listForwardMonthKeys(date = new Date(), count = 12) {
  const d = date instanceof Date ? date : new Date();
  const y = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const keys = [];
  const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 12;
  for (let i = 0; i < n; i += 1) {
    const cursor = new Date(Date.UTC(y, month + i, 1));
    keys.push(utcMonthKey(cursor));
  }
  return keys;
}

/**
 * @param {string} monthKey YYYY-MM
 * @returns {string}
 */
export function formatMonthKeyLabel(monthKey) {
  const key = typeof monthKey === 'string' ? monthKey.trim() : '';
  const match = /^(\d{4})-(\d{2})$/.exec(key);
  if (!match) return key || '—';
  const y = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(y) || m < 1 || m > 12) return key;
  const label = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return label;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, number>}
 */
export function normalizeAiSpendBudgetByMonth(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  /** @type {Record<string, number>} */
  const out = {};
  Object.keys(raw).forEach((key) => {
    const monthKey = typeof key === 'string' ? key.trim() : '';
    if (!/^\d{4}-\d{2}$/.test(monthKey)) return;
    const amount = normalizeOptionalBudgetUsd(raw[key]);
    if (amount != null) out[monthKey] = amount;
  });
  return out;
}

/**
 * Read companies/{id}.settings.aiSpendBudgets + legacy monthlyAiBudgetUsd.
 * @param {object|null|undefined} settings
 * @returns {{
 *   dailyUsd: number | null,
 *   weeklyUsd: number | null,
 *   monthlyUsd: number | null,
 *   byMonth: Record<string, number>,
 * }}
 */
export function readAiSpendBudgetsFromSettings(settings) {
  const root = settings && typeof settings === 'object' ? settings : {};
  const nested =
    root.aiSpendBudgets && typeof root.aiSpendBudgets === 'object'
      ? root.aiSpendBudgets
      : {};
  const legacyMonthly = normalizeMonthlyAiBudgetUsd(root.monthlyAiBudgetUsd);
  const monthlyUsd =
    normalizeOptionalBudgetUsd(nested.monthlyUsd) ?? legacyMonthly;
  return {
    dailyUsd: normalizeOptionalBudgetUsd(nested.dailyUsd),
    weeklyUsd: normalizeOptionalBudgetUsd(nested.weeklyUsd),
    monthlyUsd,
    byMonth: normalizeAiSpendBudgetByMonth(nested.byMonth),
  };
}

/**
 * Effective monthly budget for a given YYYY-MM: per-month override, else default.
 * @param {{ monthlyUsd?: number | null, byMonth?: Record<string, number> }} budgets
 * @param {string} [monthKey]
 * @returns {number | null}
 */
export function resolveMonthBudgetUsd(budgets, monthKey = utcMonthKey()) {
  const key = typeof monthKey === 'string' ? monthKey.trim() : utcMonthKey();
  const byMonth =
    budgets && budgets.byMonth && typeof budgets.byMonth === 'object'
      ? budgets.byMonth
      : {};
  if (Object.prototype.hasOwnProperty.call(byMonth, key)) {
    return normalizeOptionalBudgetUsd(byMonth[key]);
  }
  return normalizeOptionalBudgetUsd(budgets && budgets.monthlyUsd);
}

/**
 * @param {unknown} value
 * @returns {Date | null}
 */
function parseCreatedAt(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * @param {Date} date
 * @returns {string}
 */
function dayKeyFromDate(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * @param {Array<object>} logs
 * @returns {{
 *   byDay: Array<{ day: string, spendUsd: number, callCount: number }>,
 *   byModel: Array<{ model: string, spendUsd: number, callCount: number }>,
 *   byTicket: Array<{ ticketId: string|null, spendUsd: number, callCount: number }>,
 * }}
 */
export function groupAiUsageLogs(logs) {
  const dayMap = new Map();
  const modelMap = new Map();
  const ticketMap = new Map();

  const bump = (map, key, spend) => {
    const prev = map.get(key) || { spendUsd: 0, callCount: 0 };
    prev.spendUsd += spend;
    prev.callCount += 1;
    map.set(key, prev);
  };

  for (const row of Array.isArray(logs) ? logs : []) {
    const spend =
      typeof row.costUsd === 'number' && Number.isFinite(row.costUsd)
        ? row.costUsd
        : 0;
    const created = parseCreatedAt(row.createdAt) || new Date(0);
    bump(dayMap, dayKeyFromDate(created), spend);

    const model =
      typeof row.model === 'string' && row.model.trim()
        ? row.model.trim()
        : '(unknown)';
    bump(modelMap, model, spend);

    const ticketId =
      typeof row.ticketId === 'string' && row.ticketId.trim()
        ? row.ticketId.trim()
        : null;
    bump(ticketMap, ticketId || '(none)', spend);
  }

  const byDay = [...dayMap.entries()]
    .map(([day, v]) => ({ day, ...v }))
    .sort((a, b) => a.day.localeCompare(b.day));

  const byModel = [...modelMap.entries()]
    .map(([model, v]) => ({ model, ...v }))
    .sort((a, b) => b.spendUsd - a.spendUsd);

  const byTicket = [...ticketMap.entries()]
    .map(([ticketId, v]) => ({
      ticketId: ticketId === '(none)' ? null : ticketId,
      ...v,
    }))
    .sort((a, b) => b.spendUsd - a.spendUsd);

  return { byDay, byModel, byTicket };
}
